<script lang="ts" setup>
import { defineComponent, defineEmits, ref } from 'vue'
import { onBeforeRouteUpdate } from 'vue-router'

import { Background } from '@base/_types/Background'

import BackgroundWrapper from '@base/components/BackgroundWrapper/BackgroundWrapper.vue'
import ImageWrapper from '@base/components/ImageWrapper/ImageWrapper.vue'
import USButton from '@base/components/USButton/USButton.vue'
import USInputCheckbox from '@base/components/USInputCheckbox/USInputCheckbox.vue'
import USInputText from '@base/components/USInputText/USInputText.vue'

import SplitContent from '@player/_components/SplitContent/SplitContent.vue'

import registerStep2 from '@player/users/assets/register-step-2.png'

import blobBg from '@player/users/assets/register-step-5-blob-bg.svg'

import { staticRouteNames } from '@player/static/router/routeNames'

defineComponent({
  name: 'RegisterStep2'
})

const emits = defineEmits(['next', 'previous', 'createAccount'])

const registerFields = ref({
  username: '',
  password: '',
  confirmPassword: '',
  acceptTerms: false
})
const usernameError = ref('')
const passwordError = ref('')
const confirmPasswordError = ref('')
const acceptTermsError = ref('')

const bgImage: Background = {
  fullWidth: true,
  overflow: true,
  media: [
    {
      type: 'image',
      position: {
        alignHorizontal: 'left_50',
        alignVertical: 'center',
        centerAnchor: false,
        cover: false,
        foreground: false,
        objectPositionHorizontal: 'center',
        objectPositionVertical: 'center'
      },
      imageSet: {
        mobile: {
          fadeIn: true,
          lazy: true,
          src: blobBg
        }
      }
    }
  ]
}

const reset = () => {
  registerFields.value.username = ''
  registerFields.value.password = ''
  registerFields.value.confirmPassword = ''
  registerFields.value.acceptTerms = false
  usernameError.value = ''
  passwordError.value = ''
  confirmPasswordError.value = ''
}

const handleCreateAccountButton = () => {
  registerFields.value.username = registerFields.value.username.trim();
  
  if (!registerFields.value.username) {
    usernameError.value = 'Please input a username'
  } else {
    if(!(/^[a-zA-Z0-9]+$/).test(registerFields.value.username)) {
      usernameError.value = 'Username must only include letters and numbers'
    } else {
      usernameError.value = ''
    }
  }

  if (!registerFields.value.password) {
    passwordError.value = 'Please enter a password'
  } else {
    passwordError.value = ''
  }

  if (!registerFields.value.confirmPassword) {
    confirmPasswordError.value = 'Please reenter your password to confirm it'
  } else {
    confirmPasswordError.value = ''
  }

  if (passwordError.value !== confirmPasswordError.value) {
    passwordError.value = "These passwords don't match"
    confirmPasswordError.value = "These passwords don't match"
  } else {
    passwordError.value = ''
    confirmPasswordError.value = ''
  }

  if (!registerFields.value.acceptTerms) {
    acceptTermsError.value = 'Please accept the terms and conditions'
  } else {
    acceptTermsError.value = ''
  }

  if (
    usernameError.value ||
    passwordError.value ||
    confirmPasswordError.value ||
    acceptTermsError.value
  )
    return

  emits('createAccount', registerFields.value)
}

reset()
</script>

<template>
  <div class="register-step-2">
    <BackgroundWrapper :background="bgImage" />
    <SplitContent
      :sticky="true"
      top-offset="var(--player-header-height)"
      class="margin-top--5 margin-bottom--5"
    >
      <template #left>
        <div class="register-step-2__form-wrapper">
          <h1 class="heading--h3 margin-bottom--5">
            Step 2: Create your username
          </h1>
          <USInputText
            v-model="registerFields.username"
            name="username"
            heading="Username"
            class="register-step-2__input"
            input-id="username"
            :error="usernameError"
            input-type="text"
            :required="true"
          />
          <USInputText
            v-model="registerFields.password"
            name="password"
            heading="Password"
            class="register-step-2__input"
            input-id="password"
            minlength="6"
            :error="passwordError"
            input-type="password"
            :required="true"
          />
          <USInputText
            v-model="registerFields.confirmPassword"
            name="confirmPassword"
            heading="Confirm Password"
            class="register-step-2__input"
            input-id="confirmPassword"
            :error="confirmPasswordError"
            minlength="6"
            input-type="password"
            :required="true"
          />
          <USInputCheckbox
            v-model="registerFields.acceptTerms"
            class="margin-bottom--5"
            name="acceptTerms"
            input-id="acceptTerms"
            :error="acceptTermsError"
          >
            <template #label-slot>
              <span>
                I accept the
                <router-link
                  class="body--hyperlink--semibold"
                  :to="{ name: staticRouteNames.Terms }"
                >
                  Terms & Conditions
                </router-link>
              </span>
            </template>
          </USInputCheckbox>
          <div class="register-step-2__buttons">
            <div class="register-step-2__button">
              <USButton
                is="button"
                label="Create Account"
                @click="handleCreateAccountButton"
              />
            </div>
            <div class="register-step-2__button">
              <USButton
                is="button"
                label="Back"
                type="inline"
                @click="$emit('previous')"
              />
            </div>
          </div>
        </div>
      </template>
      <template #right>
        <div class="register-step-2__image-right">
          <ImageWrapper :src="registerStep2" aspect-ratio="1" />
        </div>
      </template>
    </SplitContent>
  </div>
</template>

<style lang="scss" scoped>
@use '@base/styles/v1.0/scss/foundations/breakpoint' as bp;

.register-step-2 {
  color: var(--color--neutral-a1);

  &__input {
    margin-bottom: var(--spacing--6);
  }

  &__image-right {
    margin-right: auto;
    margin-left: auto;
    max-width: 370px;
    margin-top: var(--spacing--5);

    @include bp.min-width(bp.$breakpoint--7) {
      margin-top: 0;
    }
  }

  &__buttons {
    align-items: center;
    display: flex;
    flex-flow: column;
    justify-content: flex-start;

    @include bp.min-width(bp.$breakpoint--7) {
      flex-flow: row;
      justify-content: space-between;
    }
  }

  &__button {
    margin-top: var(--spacing--6);

    @include bp.min-width(bp.$breakpoint--7) {
      margin-top: 0;
    }
  }
}

:deep(.split-content__content) {
  flex-flow: column-reverse;

  @media (min-width: bp.$breakpoint--tablet) {
    flex-flow: row wrap;
  }
}
</style>
